import { createAction } from 'redux-actions';
import _ from 'lodash';
//import moment from 'moment-timezone';

import { createSelector } from 'reselect';

import SwaggerAPI from 'data/SwaggerAPI';
import Devices from './Devices';
import Fields from './Fields';
import { TimePeriods } from 'utils/ReadingsHelpers';

const REQUEST_TELEMETRY = 'telemetry/REQUEST_TELEMETRY';
const REQUEST_AVG_TELEMETRY = 'telemetry/REQUEST_AVG_TELEMETRY';
const RECEIVE_LAYER_AVERAGE = 'telemetry/RECEIVE_LAYER_AVERAGE';
const END_REQUEST_TELEMETRY = 'telemetry/END_REQUEST_TELEMETRY';
const SET_TELEMETRY_PERIOD = 'telemetry/SET_PERIOD'; // "pastweek" = 0, "yesterday" = 1, "today" = 2

const initialState = {
  isFetching: false,
  telemetry: {},
  activeTelemetry: undefined,
  activeAverages: undefined,
  telemetryStart: undefined,
  telemetryEnd: undefined,
  deviceId: undefined,
  averages: { fields: {}, devices: {} },
  timePeriod: TimePeriods.OneDay,
};

export const reducer = (state = initialState, action) => {
  switch (action.type) {
    case REQUEST_TELEMETRY: {
      return {
        ...state,
        isFetching: true,
        // Clear all device data when making a new request
        averages: {
          ...state.averages,
          devices: {},
        },
      };
    }
    case END_REQUEST_TELEMETRY: {
      return { ...state, isFetching: false };
    }
    case REQUEST_AVG_TELEMETRY: {
      return { ...state, isFetching: true };
    }

    case SET_TELEMETRY_PERIOD: {
      const timePeriod = action.period;
      return {
        ...state,
        timePeriod,
      };
    }
    case Devices.SET_SELECTED_DEVICE: {
      if (action.device && !!state.telemetry && !!state.telemetry[action.device]) {
        const activeTelemetry = state.telemetry[action.device];
        const activeAverages = state.averages[action.device];
        return { ...state, activeTelemetry, activeAverages };
      }
      return state;
    }
    case RECEIVE_LAYER_AVERAGE: {
      if (!action.payload) return state;
      if (action.error) {
        return {
          ...state,
          isFetching: false,
          error: action.payload,
        };
      }

      let averages;
      if (action.payload.deviceId) {
        // Clear old device data and set new data
        const newDevices = { ...state.averages.devices };
        newDevices[action.payload.deviceId] = action.payload.telemetry;
        averages = { ...state.averages, devices: newDevices };
      } else {
        // Clear old field data and set new data
        const newFields = { ...state.averages.fields };
        newFields[action.payload.fieldId] = action.payload.telemetry;
        averages = { ...state.averages, fields: newFields };
      }

      return {
        ...state,
        isFetching: false,
        averages,
        telemetryStart: action.payload.telemetryStart,
        telemetryEnd: action.payload.telemetryEnd,
        error: undefined,
      };
    }

    default:
      return state;
  }
};

const fetchTelemetryAPI = async (layerIds, days, readingType, bucket_period, bucket_unit, include_today) => {
  let telemetryStart = Infinity;
  let telemetryEnd = 0;

  // Process telemetry if we have either field_id or hw_address
  if (layerIds.field_id || layerIds.hw_address) {
    const json = await SwaggerAPI.getDeviceTelemetry(
      layerIds,
      days,
      readingType,
      bucket_period,
      bucket_unit,
      include_today
    ); // eslint-disable-line

    if (json && json.telemetry_averages) {
      return {
        telemetry: json.telemetry_averages,
        deviceId: layerIds.hw_address,
        fieldId: layerIds.field_id,
        telemetryStart,
        telemetryEnd,
        bucket_period,
        bucket_unit,
      };
    }
  }
  return {
    telemetry: [],
    fieldId: layerIds.field_id,
    telemetryStart,
    telemetryEnd,
  };
};

export const requestTelemetry = () => ({ type: REQUEST_TELEMETRY });
export const endRequestTelemetry = () => ({ type: END_REQUEST_TELEMETRY });
export const fetchLayerAvgTelemetry = createAction(RECEIVE_LAYER_AVERAGE, fetchTelemetryAPI);

export const setTelemetryPeriod = (period) => ({
  type: SET_TELEMETRY_PERIOD,
  period,
});

const Telemetry = (function () {
  const getAllTelemetry = (state) => state.telemetry.telemetry;
  const getDeviceTelemetry = (state) => state.telemetry?.averages?.devices;
  const getFieldTelemetry = (state) => state.telemetry?.averages?.fields;
  const getIsFetching = (state) => state.telemetry.isFetching;
  const getTelemetryPeriod = (state) => state.telemetry.timePeriod;
  const getAverages = (state) => state.telemetry?.averages;

  const getActiveDeviceTelemetry = createSelector(
    [Devices.getActiveDevice, getDeviceTelemetry],
    (selectedDevice, telemetry) => {
      return selectedDevice ? telemetry[selectedDevice.hw_address] : {};
    }
  );

  const getActiveFieldTelemetry = createSelector(
    [Fields.getActiveFieldId, getFieldTelemetry],
    (selectedField, telemetry) => {
      return selectedField ? telemetry[selectedField] : {};
    }
  );

  const getAverage = createSelector([Devices.getActiveDevice, getAverages], (selectedDevice, averages) => {
    return selectedDevice ? averages.devices[selectedDevice.hw_address] : {};
  });

  const getFieldAverage = createSelector([Fields.getActiveFieldId, getAverages], (selectedField, averages) => {
    return selectedField ? averages.fields[selectedField] : {};
  });

  const getActiveTelemetry = createSelector(
    [getAllTelemetry, Fields.getFieldDevices, Devices.getActiveDevice],
    (telemetry, devices, selectedDevice) => {
      if (selectedDevice && telemetry && _.has(telemetry, selectedDevice.hw_address)) {
        return { [selectedDevice]: telemetry[selectedDevice.hw_address] };
      }
      const returnVal = {};
      _.forEach(devices, (device) => {
        if (_.has(telemetry, device.hw_address)) {
          returnVal[device.hw_address] = telemetry[device.hw_address];
        }
      });
      return returnVal;
    }
  );

  return {
    getAllTelemetry,
    getIsFetching,
    getActiveDeviceTelemetry,
    getActiveFieldTelemetry,
    getActiveTelemetry,
    getAverages,
    getAverage,
    getFieldAverage,
    getFieldTelemetry,
    getDeviceTelemetry,
    getTelemetryPeriod,
    fetchLayerAvgTelemetry,
    requestTelemetry,
    endRequestTelemetry,
  };
})();

export default Telemetry;
